import { useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent, StyleSheet, TextLayoutEvent } from 'react-native';
import Animated, { SharedValue, interpolateColor, useAnimatedStyle } from 'react-native-reanimated';

import { Text } from '@/components/TextComponent';
import { Box, Image, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { common, designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';
import { isTextClipped } from '@/utils/is-text-clipped';

import {
    LABEL_LINE_HEIGHT,
    LABEL_SELECTED_WEIGHT,
    LEAGUE_ITEM_SLOT,
    LeagueItemLayout,
    TEXT_GRADIENT_WIDTH,
} from '../utils/league-navigator-utils';
import { EdgeFade } from './EdgeFade';
import { LeagueNavigatorItem } from './LeagueNavigator';

const AnimatedText = Animated.createAnimatedComponent(Text);

const webClipStyle = { textOverflow: 'clip' };

type LeagueItemProps = {
    league: LeagueNavigatorItem;
    layout: LeagueItemLayout;
    isSelected: boolean;
    indicatorX: SharedValue<number>;
    onPress: () => void;
    testID?: string;
};

export const LeagueItem = ({ league, layout, isSelected, indicatorX, onPress, testID }: LeagueItemProps) => {
    const [isClipped, setIsClipped] = useState(false);
    const fontWeight = isSelected ? LABEL_SELECTED_WEIGHT : undefined;

    const labelColorRange = useMemo(
        () => [
            layout.indicatorOffset - LEAGUE_ITEM_SLOT,
            layout.indicatorOffset,
            layout.indicatorOffset + LEAGUE_ITEM_SLOT,
        ],
        [layout.indicatorOffset]
    );

    const labelColorStyle = useAnimatedStyle(() => {
        return {
            color: interpolateColor(indicatorX.value, labelColorRange, [
                designSystem.colors.neutral300,
                ds.colorValue.text.primary,
                designSystem.colors.neutral300,
            ]),
        };
    }, [labelColorRange]);

    const onTextLayout = useCallback((event: TextLayoutEvent) => {
        setIsClipped(isTextClipped(event));
    }, []);

    const onLayout = useCallback((event: LayoutChangeEvent) => {
        setIsClipped(event.nativeEvent.layout.height > LABEL_LINE_HEIGHT);
    }, []);

    const label = (
        <AnimatedText
            numberOfLines={1}
            ellipsizeMode={'clip'}
            variant={'labelMedium'}
            fontWeight={fontWeight}
            lineHeight={LABEL_LINE_HEIGHT}
            // @ts-ignore textOverflow only works on web
            style={isWeb ? [labelColorStyle, webClipStyle] : labelColorStyle}
        >
            {league.label}
        </AnimatedText>
    );

    return (
        <TouchableBox
            onPress={onPress}
            gap={'s8'}
            alignItems={'center'}
            width={layout.width}
            disabled={league.disabled}
            opacity={league.disabled ? 0.3 : 1}
            testID={testID}
        >
            <Box p={'s6'}>
                <Image source={league.image} resizeMode={'contain'} style={styles.image} />
            </Box>
            {isClipped ? (
                <EdgeFade width={layout.width} right={TEXT_GRADIENT_WIDTH}>
                    {label}
                </EdgeFade>
            ) : (
                label
            )}
            {league.fitsLabel ? null : (
                <Text
                    variant={'labelMedium'}
                    fontWeight={fontWeight}
                    // onTextLayout doesn't work on web so we use onLayout
                    onLayout={isWeb ? onLayout : undefined}
                    onTextLayout={onTextLayout}
                    style={common.measureLayer}
                    lineHeight={LABEL_LINE_HEIGHT}
                    accessibilityElementsHidden
                    importantForAccessibility={'no-hide-descendants'}
                >
                    {league.label}
                </Text>
            )}
        </TouchableBox>
    );
};

const styles = StyleSheet.create({
    image: {
        width: 32,
        height: 32,
    },
});
